import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Home, Leaf, ListChecks, BookOpen, Sparkles } from 'lucide-react';
import { tasksApi, getDesignerCoEdit, clearDesignerCoEdit } from './services/gardenApi';

// Phone stays a readable column; from tablet up the shell grows with the viewport
// so iPad and desktop are not stuck in a ~448px strip.
const SHELL_MAX =
  'mx-auto w-full max-w-md md:max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl';

const TABS = [
  { to: '/garden',          label: 'Garden',  Icon: Home,        matchPrefix: true },
  { to: '/garden/plants',   label: 'Plants',  Icon: Leaf },
  { to: '/garden/tasks',    label: 'Tasks',   Icon: ListChecks,  badge: 'tasks' },
  { to: '/garden/journal',  label: 'Journal', Icon: BookOpen },
  { to: '/garden/ai',       label: 'AI Help', Icon: Sparkles },
];

// The "Garden" tab should remain active on both the home grid (/garden) and any
// zone detail page (/garden/zones/:id).
function isGardenHomeActive(pathname) {
  if (pathname === '/garden' || pathname === '/garden/') return true;
  if (pathname.startsWith('/garden/zones')) return true;
  return false;
}

function useOpenTasksThisMonth() {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const r = await tasksApi.list('this_month');
      // Only action tasks count toward the badge; informational events
      // (bloom start, dormancy) are context, not something to do.
      const open = (r.tasks || []).filter(
        (t) => !t.completed && !t.is_informational
      ).length;
      setCount(open);
    } catch {
      // Silent: a failed badge fetch should never block navigation. If auth
      // is missing or the server is down, just hide the badge.
      setCount(0);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener('garden:tasks-changed', onChange);
    return () => window.removeEventListener('garden:tasks-changed', onChange);
  }, [refresh]);

  return count;
}

function useCoEdit() {
  const [coEdit, setCoEdit] = useState(() => getDesignerCoEdit());
  useEffect(() => {
    const onChange = () => setCoEdit(getDesignerCoEdit());
    window.addEventListener('garden:co-edit-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('garden:co-edit-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return coEdit;
}

export default function GardenShell() {
  const location = useLocation();
  const openTasks = useOpenTasksThisMonth();
  const coEdit = useCoEdit();

  return (
    <div className="min-h-screen bg-garden-surface text-garden-ink">
      {coEdit ? (
        <div
          role="status"
          className="sticky top-0 z-40 w-full bg-amber-100 border-b border-amber-300 text-amber-900 text-sm"
        >
          <div className="mx-auto max-w-md px-3 py-2 flex items-center gap-2">
            <span role="img" aria-label="designer">🧑‍🎨</span>
            <span className="truncate">
              Designer co-edit mode{coEdit.label ? ` — ${coEdit.label}` : ''}
            </span>
            <button
              type="button"
              onClick={clearDesignerCoEdit}
              className="ml-auto underline font-medium"
              title="Exit co-edit mode and return to your own garden"
            >
              Exit
            </button>
          </div>
        </div>
      ) : null}
      <div className={`${SHELL_MAX} min-h-screen relative pb-20`}>
        <Outlet />
      </div>
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-black/5"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className={`${SHELL_MAX} grid grid-cols-5`}>
          {TABS.map(({ to, label, Icon, matchPrefix, badge }) => {
            const prefixActive = matchPrefix ? isGardenHomeActive(location.pathname) : false;
            const badgeCount = badge === 'tasks' ? openTasks : 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={!matchPrefix && to === '/garden'}
                className={({ isActive }) => {
                  const active = matchPrefix ? prefixActive : isActive;
                  return (
                    `min-h-[56px] flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium ` +
                    (active ? 'text-garden-primary' : 'text-garden-muted')
                  );
                }}
              >
                <span className="relative inline-flex">
                  <Icon size={22} strokeWidth={2} />
                  {badgeCount > 0 ? (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-garden-primary text-white text-[10px] font-semibold leading-[18px] text-center shadow-sm"
                      aria-label={`${badgeCount} open tasks`}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  ) : null}
                </span>
                <span>{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
